import { isEqual as dateFnsIsEqual } from "date-fns";
import lodash from "lodash";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import { Box, Button, Divider, Grid, Icon, IconButton, Stack, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SAVE_COMMENT from "../../../graphql/mutations/ProjectEditContext/saveComment";
import TipTapEditor from "../../common/TipTapEditor";
import { TimeLogAction } from "../helper";
import { ColumnTypes } from "../helpers/columnUtils";
import CommentBox from "./CommentBox";
import DescriptionField from "./DescriptionField";
import NameField from "./NameField";
import TaskDetailsGrid from "./TaskDetailsGrid";

const TaskDetailsBase = ({
  columns,
  currentProject,
  disabled,
  enableEdit,
  item,
  onDelete,
  onSave,
  onTimeLogAction,
  renderComponent: RenderComponent,
  userLookup,
}) => {
  const [dirtyProperties, setDirtyProperties] = useState({
    assignments: false,
    createdBy: false,
    description: false,
    endDate: false,
    name: false,
    startDate: false,
  });
  const intl = useIntl();

  const [editItem, setEditItem] = useState();
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [reset, setReset] = useState(false);

  const [saveComment, { data, loading, error }] = useMutation(SAVE_COMMENT);

  useEffect(() => {

    if (data) {
      setCurrentComment("");
    }
    else if (loading) {

    }
    else if (error) {

    }


  }, [data, loading, error])

  const handleTaskAssignmentPropertyChange = (name, value) => {
    const originalValue = item[name] ? item[name][0] : null;
    const editValue = editItem[name] ? editItem[name][0] : null;

    if (lodash.isEqual(value, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: false,
      }));
    }
    else if (lodash.isEqual(editValue, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: true,
      }));
    }

    setEditItem(prev => ({
      ...prev,
      [name]: value ? [value] : [],
    }));
  };

  const handleTaskDatePropertyChange = (name, value) => {
    const isDateEqual = (first, second) => {
      const dateOnly = (date) => {
        var o = new Date(date);
        o.setHours(0, 0, 0, 0);
        return o;
      }
      return dateFnsIsEqual(dateOnly(first), dateOnly(second));
    }

    const originalValue = item[name];
    const editValue = editItem[name];

    if ((!value && !originalValue) || isDateEqual(value, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: false,
      }));
    }
    else if ((!editValue && !originalValue) || isDateEqual(editValue, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: true,
      }));
    }

    setEditItem(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTaskPropertyChange = (name, value) => {
    const originalValue = item[name];
    const editValue = editItem[name];

    if (lodash.isEqual(value, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: false,
      }));
    }
    else if (lodash.isEqual(editValue, originalValue)) {
      setDirtyProperties(prev => ({
        ...prev,
        [name]: true,
      }));
    }

    setEditItem(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTaskPropertyDirty = (name, dirty) => {
    setDirtyProperties(prev => ({
      ...prev,
      [name]: dirty,
    }));
  };

  const handleTaskPropertyValidationChange = (name, valid) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      if (valid) {
        newSet.delete(name);
      }
      else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const handleTaskValueChange = (fieldId, value) => {
    var field = currentProject.projectFields.find(f => f.fieldId === fieldId);
    var valueField = '';

    switch (field.type) {
      case ColumnTypes.TIME_REPORT: // Duration
      case ColumnTypes.NUMBER:
        valueField = 'numericValue';
        break;

      case ColumnTypes.DATE:
        valueField = 'dateTimeValue';
        break;

      case ColumnTypes.STRING:
      case ColumnTypes.STATUS:
      default:
        valueField = 'stringValue';
        break;
    }

    setDirtyProperties(prev => ({
      ...prev,
      [`_${fieldId}`]: true,
    }));

    setEditItem(prev => {
      let values = lodash.cloneDeep(prev.values);

      if (!values) {
        values = [{ fieldId, [valueField]: value }];
      } else {
        const existingIndex = values.findIndex(taskValue => taskValue.fieldId === fieldId);

        if (existingIndex !== -1) {
          // If the fieldId already exists, update its value
          (values[existingIndex])[valueField] = value;
        } else {
          // If the fieldId doesn't exist, add a new object to the values array
          values.push({ fieldId, [valueField]: value });
        }
      }

      return {
        ...prev,
        values,
      }
    });
  };

  const handleUpdateTimeLog = (task, reportItem, minutes, date) => {
    onTimeLogAction(TimeLogAction.UPDATE, { task, reportItem, minutes, date });
  }

  const handleAddTimeLog = (task, minutes, date) => {
    onTimeLogAction(TimeLogAction.ADD, { task, minutes, date });
  }

  const handleDeleteTimeLog = (task, timeLogId) => {
    onTimeLogAction(TimeLogAction.DELETE, { task, timeLogId })
  }

  useEffect(() => {
    let dirty = false;
    for (var propertyName in dirtyProperties) {
      if (dirtyProperties[propertyName]) {
        dirty = true;
        break;
      }
    }
    if (isDirty !== dirty) {
      setIsDirty(dirty);
    }
  }, [dirtyProperties]);

  useEffect(() => {
    const task = lodash.cloneDeep(item);
    setDirtyProperties({
      assignments: false,
      createdBy: false,
      description: false,
      endDate: false,
      name: false,
      startDate: false,
    });
    setEditItem(task);
    setIsDirty(false);
    setReset(true);
  }, [item]);

  useEffect(() => {
    if (reset) {
      setReset(false);
    }
  }, [reset]);

  const [currentComment, setCurrentComment] = useState();

  const handleNewComment = () => {

    /*let newList = [...comments, { id: 0, timestamep: "banan", comment: currentComment }];
    setCurrentComment('');
    setComments(newList);*/

    saveComment({
      variables: {
        comment: {
          taskId: item.id,
          comment: currentComment
        }
      }
    });

  }

  const DeleteButton = ({ disabled, onDelete }) => (
    <Tooltip
      arrow
      disabled={disabled}
      placement="left"
      title={<FormattedMessage id="project.taskContextMenu.deleteTask" />}
      PopperProps={{
        modifiers: [{ name: "offset", options: { offset: [0, -8] } }]
      }}
    >
      <IconButton onClick={onDelete}>
        <DeleteIcon sx={{ opacity: 0.4, "&:hover": { opacity: 1 } }} />
      </IconButton>
    </Tooltip>
  );

  // Assuming RenderComponent is a component, not a string

  return item && editItem && (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", ml: "1px" }}>
        <NameField
          sx={{ width: "100%" }}
          disabled={disabled}
          enableEdit={enableEdit}
          onChange={({ value }) => handleTaskPropertyChange("name", value)}
          onDirty={({ dirty }) => handleTaskPropertyDirty("name", dirty)}
          reset={reset}
          value={item.name}
        />
        <DeleteButton
          disabled={item.id === 0}
          onDelete={() => onDelete?.({ taskId: editItem.id })}
        />
      </Box>
      <DescriptionField
        disabled={disabled}
        editable={enableEdit}
        onChange={({ value }) => handleTaskPropertyChange("description", value)}
        onDirty={({ dirty }) => handleTaskPropertyDirty("description", dirty)}
        reset={reset}
        value={item.description}
      />
      <RenderComponent
        columns={columns}
        disabled={disabled}
        editItem={editItem}
        enableEdit={enableEdit}
        handleAddTimeLog={handleAddTimeLog}
        handleDeleteTimeLog={handleDeleteTimeLog}
        handleTaskAssignmentPropertyChange={handleTaskAssignmentPropertyChange}
        handleTaskDatePropertyChange={handleTaskDatePropertyChange}
        handleTaskPropertyChange={handleTaskPropertyChange}
        handleTaskPropertyValidationChange={handleTaskPropertyValidationChange}
        handleTaskValueChange={handleTaskValueChange}
        handleUpdateTimeLog={handleUpdateTimeLog}
        item={item}
        userLookup={userLookup}
      />

      <Divider sx={{ margin: "8px 0px 8px 0px", opacity: 0.2 }} />

      <Stack direction="row" justifyContent="end" sx={{ pr: "1em" }}>
        {enableEdit &&
          <Button
            disabled={disabled || !isDirty || invalidFields.size}
            onClick={() => onSave?.({ item: editItem })}
            variant="contained"
          >
          <FormattedMessage id={item.id ? "btn.save" : "btn.create"} />
          </Button>
        }
      </Stack>

      <Divider sx={{ margin: "8px 0px 8px 0px", opacity: 0.2 }} />

      <Grid container>
        <Grid item {...(RenderComponent.name === TaskDetailsGrid.name) ? { xs: 12, sm: 8, md: 6 } : { xs: 12 }}>
          <Box sx={{ position: "relative" }}>
            <TipTapEditor
              value={currentComment}
              minHeight={75}
              placeholder={intl.formatMessage({ id: "newsPost.comment" }) /* Only the initial value will be displayed. */}
              onChange={newContent => setCurrentComment(newContent)}
            />

            <IconButton
              size="small"
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                color: "primary.main",
                "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
              }}
              onClick={handleNewComment}
              disabled={loading || !currentComment?.trim()}
            >
              <Icon className="fas fa-paper-plane" fontSize="small" />
            </IconButton>
          </Box>

          {item.comments && item.comments.map(n => (
            <CommentBox key={n.id} comment={n} />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskDetailsBase;
